"use client";

import cn from "@utils/cn";
import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import React, { FC, useContext } from "react";

import EventHero from "@/components/EventComponent/EventHero";
import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
import Icon from "@/components/ui/Icon/Icon";
import { TemplateContext } from "@/context/TemplateContext";
import useMediaQuery from "@/hooks/useMediaQuery";
import { EditorialTemplateHeroProps } from "@/types";

import Breadcrumb from "../Breadcrumb/Breadcrumb";

const EditorialTemplateHero: FC<EditorialTemplateHeroProps> = ({ entry }) => {
  const t = useTranslations();
  const locale = useLocale();
  const { fixedHeader } = useContext(TemplateContext);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isEvent = entry?.variant === "Event";

  if (isEvent) {
    return (
      <EventHero
        tag={entry?.tags?.[0] || ""}
        title={entry?.title || ""}
        eventDate={entry?.eventDate || ""}
        location={entry?.eventLocaltion || ""}
        countdownEndDate={entry?.eventCountdownEndDate}
        backgroundImage={{
          src: entry?.image?.url || "",
          alt: entry?.image?.title || "",
        }}
        registerHref="#event-register"
        locale={locale}
        cta={entry?.cta}
        eventExpireText={entry?.eventExpireText}
        timeRemaining={entry?.timeRemaining}
      />
    );
  }

  if (entry?.tags) {
    entry.tags = entry?.tags.slice(0, 3);
  }

  const share = async (shareData: any) => {
    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      navigator?.clipboard?.writeText(shareData?.url);
      alert(t("copy.link"));
      console.log("share not supported");
    }
  };

  const mediaExist = !!(entry?.image || entry?.video);
  const mediaExistForMobile = !!(entry?.imageMobile || entry.video);

  return (
    <div
      className={cn(
        "p-m ",
        // md:p-0
        // ",
        // Apply mediaExist styles if it's desktop, otherwise apply mediaExistForMobile styles for mobile
        isDesktop
          ? mediaExist && "relative bg-cover bg-center text-white h-svh md:h-[100vh]"
          : mediaExistForMobile && "relative bg-cover bg-center text-white h-svh md:h-[100vh]",
      )}
      style={
        // Use entry.image for desktop, and entry.imageMobile for mobile if available, otherwise fallback to entry.image
        isDesktop
          ? entry?.image && { backgroundImage: `url(${entry?.image?.url}` }
          : entry?.imageMobile
            ? { backgroundImage: `url(${entry?.imageMobile?.url}` }
            : entry?.image && { backgroundImage: `url(${entry?.image?.url}` } // Fallback for mobile if imageMobile is not present
      }
    >
      {entry?.video && (
        <div className="absolute w-full h-full">
          <video className="h-full w-full object-cover" muted autoPlay loop playsInline title={entry?.video?.title}>
            <source src={entry?.video?.url} type="video/mp4" />
          </video>
        </div>
      )}

      {mediaExist && <div className="absolute z-10 w-full h-full img-overlay-article-hero top-0 left-0 pt-"></div>}
      <div
        className={cn(
          "px-s md:px-0 md:w-96 md:mx-auto pb-xxl text-center",
          mediaExist ? "pt-m md:pt-xl" : "pt-l md:pt-3xl",
          fixedHeader ? "!pt-xxl md:!pt-[72px]" : "!pt-0 md:pt-xl",
        )}
      >
        <div className={cn(!mediaExist && "md:flex md:items-center md:justify-center md:relative md:mb-xxl")}>
          {mediaExist ? (
            <div
              className={`flex items-center justify-between z-20 mb-l relative`}
              style={isDesktop ? { marginTop: "20px", padding: "0 18px" } : { marginTop: "60px" }}
            >
              {entry?.breadcrumb && <Breadcrumb entry={entry?.breadcrumb} disabledBehaviour={true} />}
              <button
                onClick={() =>
                  share({ title: entry?.title || entry.logo?.title, text: entry?.title || entry.logo?.title, url: window?.location?.href })
                }
              >
                <Icon name="share" size={"20"} className={cn(mediaExist ? "stroke-white" : "stroke-black")}></Icon>
              </button>
            </div>
          ) : (
            <AnchorLink
              href={getLink(entry?.breadcrumb?.breadcrumbItems[entry?.breadcrumb?.breadcrumbItems?.length - 1], locale).toString()}
              className="underline underline-offset-2 text-s font-medium inline-block md:absolute md:left-0 md:top-[50%] md:translate-y-[-50%] mb-xl md:mb-0"
            >
              <>{t("label.common.backTo", { label: entry?.breadcrumb?.breadcrumbItems[entry?.breadcrumb?.breadcrumbItems?.length - 1]?.label })}</>
            </AnchorLink>
          )}
          <div className={cn(!mediaExist && "md:flex items-center md:gap-xxl ")}>
            {entry?.logo && (
              <Image
                src={entry?.logo?.url}
                width={170}
                height={80}
                alt={entry?.logo?.title || ""}
                className={cn(
                  "mx-auto my-xxl md:m-0",
                  mediaExist && "absolute top-[45%] translate-y-[-50%] left-[50%] translate-x-[-50%] z-10 md:w-5\\/24",
                )}
              />
            )}
            <div className={cn(mediaExist && "absolute bottom-[40px] md:bottom-[24px] z-10 left-[50%] translate-x-[-50%] w-full md:w-20\\/24")}>
              {entry?.title && (
                <h1 className={cn("font-primary uppercase text-l md:text-xl mb-xl ", !mediaExist ? "md:mb-0" : "md:mb-l font-medium")}>
                  {entry?.title}
                </h1>
              )}
              {mediaExist && entry?.tags?.length && (
                <div className="flex items-center gap-xs justify-center">
                  {entry?.tags?.map((tag, i) => (
                    <span className="text-s uppercase rounded p-[6px] text-black golden-gradient" key={`bgTag-${i}`}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              {entry?.logoScrollDownImage && (
                <div className="relative w-full flex flex-col items-center">
                  <Image
                    src={entry?.logoScrollDownImage?.url}
                    alt={entry?.logoScrollDownImage?.title}
                    width={177}
                    height={90}
                    style={{ height: "90px" }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {!mediaExist && entry?.abstract && <p className="font-secondary-light text-[14px]">{entry?.abstract}</p>}
      </div>
    </div>
  );
};

export default EditorialTemplateHero;
